/**
 * SetupWizardPage — Betriebs-Onboarding (Einrichtungs-Assistent) für Studios.
 * Route: /einrichtung (Rolle: admin). 7 Schritte, Fortschritt serverseitig.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Building2, Palette, Package, ArrowRightLeft, CreditCard, CalendarCheck,
  Check, Upload, Loader2, ChevronRight, ChevronLeft, Sparkles, FileCheck2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { getSelectedBranch } from '@/lib/branchTheme';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_META = [
  { key: 'branche', label: 'Branche', icon: Building2 },
  { key: 'firma', label: 'Firma', icon: Building2 },
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'module', label: 'Module', icon: Package },
  { key: 'wechsel', label: 'Wechsel', icon: ArrowRightLeft },
  { key: 'zahlung', label: 'Testmonat', icon: CreditCard },
  { key: 'fertig', label: 'Fertig', icon: CalendarCheck },
];

const WECHSEL_SYSTEME = [
  'SportsNow', 'Eversports', 'Fitogram', 'Magicline', 'Excel / CSV', 'Anderes System', 'Keines / Neu',
];

const stepIndex = (k) => Math.max(0, STEP_META.findIndex((s) => s.key === k));

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('branche');
  const [data, setData] = useState({});
  const [subStatus, setSubStatus] = useState('none');

  const load = useCallback(async () => {
    try {
      const { data: s } = await axios.get(`${API}/einrichtung/state`);
      setData(s.data || {});
      setSubStatus(s.subscription_status || 'none');
      if (!s.completed) setStep(s.current_step || 'branche');
      else setStep('fertig');
    } catch (e) {
      toast.error('Einrichtung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  // Rückkehr von Stripe (?status=success&session_id=...)
  const polled = useRef(false);
  useEffect(() => {
    const status = params.get('status');
    const sessionId = params.get('session_id');
    if (status === 'success' && sessionId && !polled.current) {
      polled.current = true;
      axios.get(`${API}/einrichtung/payment/status/${sessionId}`)
        .then(() => load())
        .then(() => { setStep('fertig'); toast.success('Testmonat aktiviert – deine Karte ist hinterlegt.'); })
        .catch(() => toast.error('Zahlungsstatus konnte nicht geprüft werden'))
        .finally(() => { params.delete('status'); params.delete('session_id'); setParams(params, { replace: true }); });
    } else if (status === 'cancel') {
      toast.info('Zahlung abgebrochen – du kannst es jederzeit erneut versuchen.');
      params.delete('status'); setParams(params, { replace: true });
      load();
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveStep = async (nextStep, patch = {}) => {
    setSaving(true);
    try {
      const { data: s } = await axios.put(`${API}/einrichtung/state`, {
        current_step: nextStep, data: patch,
      });
      setData(s.data || {});
      setStep(nextStep);
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    const i = stepIndex(step);
    if (i > 0) setStep(STEP_META[i - 1].key);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const idx = stepIndex(step);
  const progress = Math.round(((idx) / (STEP_META.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="setup-wizard">
      <Toaster position="top-center" richColors />
      <div className="mx-auto max-w-6xl px-4 py-8 lg:py-12">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30">
            <Sparkles className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Studio einrichten</h1>
            <p className="text-sm text-neutral-400">In wenigen Schritten startklar.</p>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
          {/* Stepper */}
          <aside className="hidden lg:block">
            <ol className="space-y-1">
              {STEP_META.map((s, i) => {
                const Icon = s.icon;
                const done = i < idx;
                const active = i === idx;
                return (
                  <li key={s.key}>
                    <div
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                        active ? 'bg-emerald-500/10 ring-1 ring-emerald-500/30'
                        : done ? 'opacity-80' : 'opacity-50'}`}
                      data-testid={`step-indicator-${s.key}`}
                    >
                      <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold ${
                        done ? 'bg-emerald-500 text-neutral-950'
                        : active ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40'
                        : 'bg-neutral-800 text-neutral-400'}`}>
                        {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                      </span>
                      <span className={`text-sm ${active ? 'font-medium text-neutral-100' : 'text-neutral-300'}`}>{s.label}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Content */}
          <main>
            <div className="mb-6 lg:hidden">
              <Progress value={progress} className="h-1.5" />
              <p className="mt-2 text-xs text-neutral-400">Schritt {idx + 1} von {STEP_META.length}</p>
            </div>

            <div className="rounded-3xl border border-neutral-800 bg-neutral-900/60 p-6 sm:p-8">
              {step === 'branche' && <BrancheStep data={data} onBranche={(key, mods, label, pt) => { setData((d) => ({ ...d, branche: key, branche_label: label, practice_type: pt, starter_module_keys: mods })); setStep('firma'); }} />}
              {step === 'firma' && <FirmaStep data={data} onBack={goBack} onSaved={(s) => { setData(s.data); setStep('branding'); }} />}
              {step === 'branding' && <BrandingStep data={data} onBack={goBack} onNext={() => saveStep('module')} />}
              {step === 'module' && <ModuleStep data={data} onBack={goBack} onNext={(sel) => saveStep('wechsel', { modules: sel })} />}
              {step === 'wechsel' && <WechselStep data={data} onBack={goBack} onNext={() => saveStep('zahlung')} reload={load} />}
              {step === 'zahlung' && <ZahlungStep data={data} subStatus={subStatus} onBack={goBack} onDone={() => setStep('fertig')} />}
              {step === 'fertig' && <FertigStep navigate={navigate} />}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Steps ----------------------------- */

function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-neutral-100">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-neutral-400">{subtitle}</p>}
    </div>
  );
}

function NavRow({ onBack, children }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? (
        <Button variant="ghost" onClick={onBack} data-testid="setup-back-btn" className="text-neutral-300 hover:text-white">
          <ChevronLeft className="mr-1 h-4 w-4" /> Zurück
        </Button>
      ) : <span />}
      {children}
    </div>
  );
}

function BrancheStep({ data, onBranche }) {
  const [branchen, setBranchen] = useState(null);
  const [sel, setSel] = useState(data.branche || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/einrichtung/branchen`)
      .then(({ data: s }) => {
        const list = s.branchen || [];
        setBranchen(list);
        // Bei der Branchen-Auswahl (öffentliche Seite) gewählte Branche vorauswählen.
        if (!data.branche) {
          const picked = getSelectedBranch();
          if (picked?.group) {
            const match = list.find((b) => b.group === picked.group);
            if (match) setSel(match.branche);
          }
        }
      })
      .catch(() => { toast.error('Branchen konnten nicht geladen werden'); setBranchen([]); });
  }, []);

  const submit = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      const { data: r } = await axios.post(`${API}/einrichtung/branche`, { branche: sel });
      const chosen = (branchen || []).find((b) => b.branche === sel);
      onBranche(sel, r.module_keys || [], chosen?.label || sel, r.practice_type);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  if (!branchen) return <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />;

  // Nach Invicta-Produktfamilie gruppieren (Server liefert bereits in GROUP_ORDER).
  const groups = [];
  const byGroup = {};
  (branchen || []).forEach((b) => {
    const g = b.group || 'Weitere';
    if (!byGroup[g]) { byGroup[g] = []; groups.push([g, byGroup[g]]); }
    byGroup[g].push(b);
  });

  return (
    <div>
      <StepHeader title="Welche Branche?" subtitle="Wir wählen passende Module als Startpaket vor – anpassen kannst du sie gleich danach." />
      <div className="space-y-6">
        {groups.map(([g, items]) => (
          <div key={g} data-testid={`branche-group-${g}`}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-400">{g}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((b) => (
                <button
                  key={b.branche}
                  onClick={() => setSel(b.branche)}
                  data-testid={`branche-option-${b.branche}`}
                  className={`rounded-2xl border px-4 py-4 text-left text-sm transition-all ${
                    sel === b.branche ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-neutral-800 bg-neutral-900 text-neutral-300 hover:border-neutral-700'}`}
                >
                  <div className="font-medium">{b.label}</div>
                  {b.description ? (
                    <div className="mt-1 text-xs text-neutral-400">{b.description}</div>
                  ) : null}
                  {Array.isArray(b.module_labels) && b.module_labels.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {b.module_labels.slice(0, 4).map((m, i) => (
                        <span key={i} className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-300">{m}</span>
                      ))}
                      {b.module_labels.length > 4 ? (
                        <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-400">+{b.module_labels.length - 4}</span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 text-[11px] text-neutral-500">
                    {b.module_keys.length} Module · ab CHF {b.price_chf}/Mt.
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <NavRow>
        <Button disabled={!sel || saving} onClick={submit} data-testid="branche-next-btn"
          className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Weiter <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </NavRow>
    </div>
  );
}

function FirmaStep({ data, onBack, onSaved }) {
  const f = data.firma || {};
  const [form, setForm] = useState({
    company_name: f.company_name || '', street: f.street || '', zip: f.zip || '',
    city: f.city || '', uid: f.uid || '', contact_email: f.contact_email || '', contact_phone: f.contact_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [zq, setZq] = useState('');
  const [zres, setZres] = useState([]);
  const [zsearching, setZsearching] = useState(false);
  const [zconfigured, setZconfigured] = useState(true);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const zefixSearch = async () => {
    if (zq.trim().length < 3) { toast.error('Bitte mindestens 3 Zeichen eingeben'); return; }
    setZsearching(true); setZres([]);
    try {
      const { data: r } = await axios.get(`${API}/einrichtung/firma/zefix-search`, { params: { name: zq.trim() } });
      setZconfigured(r.configured !== false);
      setZres(r.results || []);
      if (r.configured !== false && (r.results || []).length === 0) toast.info('Keine Treffer – bitte Felder manuell ausfüllen');
    } catch { toast.error('Suche fehlgeschlagen'); }
    finally { setZsearching(false); }
  };

  const zefixPick = async (uid) => {
    try {
      const { data: d } = await axios.get(`${API}/einrichtung/firma/zefix-detail`, { params: { uid } });
      setForm((prev) => ({
        ...prev,
        company_name: d.company_name || prev.company_name,
        street: d.street || prev.street,
        zip: d.zip || prev.zip,
        city: d.city || prev.city,
        uid: d.uid || prev.uid,
      }));
      setZres([]); setZq('');
      toast.success('Firmendaten übernommen');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übernahme fehlgeschlagen'); }
  };

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error('Firmenname wird benötigt'); return; }
    setSaving(true);
    try {
      const { data: s } = await axios.post(`${API}/einrichtung/firma`, form);
      toast.success('Firmendaten gespeichert');
      onSaved(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const field = (k, label, ph = '', type = 'text') => (
    <div>
      <Label className="text-neutral-300">{label}</Label>
      <Input value={form[k]} onChange={set(k)} placeholder={ph} type={type}
        data-testid={`firma-${k}`} className="mt-1 border-neutral-700 bg-neutral-950 text-neutral-100" />
    </div>
  );

  return (
    <div>
      <StepHeader title="Deine Firma" subtitle="Diese Angaben erscheinen auf Rechnungen und im Kundenportal." />
      <div className="mb-4 rounded-xl border border-sky-800/40 bg-sky-950/30 p-3" data-testid="firma-zefix-box">
        <Label className="text-xs text-sky-200">Firma im Handelsregister suchen (Zefix)</Label>
        <div className="mt-1 flex gap-2">
          <Input value={zq} onChange={(e) => setZq(e.target.value)} placeholder="Firmenname, z. B. Migros"
            onKeyDown={(e) => { if (e.key === 'Enter') zefixSearch(); }}
            data-testid="firma-zefix-query" className="border-neutral-700 bg-neutral-950 text-neutral-100" />
          <Button type="button" onClick={zefixSearch} disabled={zsearching} data-testid="firma-zefix-search-btn"
            className="shrink-0 bg-sky-600 text-white hover:bg-sky-500">
            {zsearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Suchen'}
          </Button>
        </div>
        {!zconfigured && <p className="mt-1.5 text-xs text-neutral-400">Zefix ist nicht aktiv – bitte Felder manuell ausfüllen.</p>}
        {zres.length > 0 && (
          <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-neutral-800" data-testid="firma-zefix-results">
            {zres.map((r, i) => (
              <button key={i} type="button" onClick={() => zefixPick(r.uid)} data-testid={`firma-zefix-result-${i}`}
                className="flex w-full items-center justify-between gap-2 border-b border-neutral-800 px-3 py-2 text-left last:border-0 hover:bg-neutral-800/60">
                <span>
                  <span className="text-sm text-neutral-100">{r.name}</span>
                  <span className="block text-xs text-neutral-400">{[r.city, r.uid].filter(Boolean).join(' · ')}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-neutral-500" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid gap-4">
        {field('company_name', 'Firmenname *', 'z. B. StrongFit AG')}
        <div className="grid gap-4 sm:grid-cols-2">
          {field('street', 'Strasse & Nr.', 'Bahnhofstrasse 1')}
          {field('uid', 'UID / MwSt-Nr.', 'CHE-123.456.789')}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('zip', 'PLZ', '8000')}
          {field('city', 'Ort', 'Zürich')}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('contact_email', 'Kontakt-E-Mail', 'info@studio.ch', 'email')}
          {field('contact_phone', 'Telefon', '+41 44 123 45 67')}
        </div>
      </div>
      <NavRow onBack={onBack}>
        <Button disabled={saving} onClick={submit} data-testid="firma-next-btn"
          className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Weiter <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </NavRow>
    </div>
  );
}

function BrandingStep({ data, onBack, onNext }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/portal/settings`).then(({ data: s }) => setSettings({
      studio_name: s.studio_name || (data.firma?.company_name) || 'Mein Studio',
      logo_url: s.logo_url || null,
      primary_color: s.primary_color || '#22c55e',
      secondary_color: s.secondary_color || '#10b981',
      accent_color: s.accent_color || '#059669',
    })).catch(() => setSettings({
      studio_name: data.firma?.company_name || 'Mein Studio', logo_url: null,
      primary_color: '#22c55e', secondary_color: '#10b981', accent_color: '#059669',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: r } = await axios.post(`${API}/portal/settings/logo`, fd);
      setSettings((p) => ({ ...p, logo_url: r.logo_url, ...(r.suggested_color ? { primary_color: r.suggested_color } : {}) }));
      toast.success('Logo hochgeladen');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/settings`, {
        studio_name: settings.studio_name,
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        accent_color: settings.accent_color,
      });
      onNext();
    } catch (e) {
      toast.error('Branding konnte nicht gespeichert werden');
    } finally { setSaving(false); }
  };

  if (!settings) return <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />;

  const colorField = (k, label) => (
    <div className="flex items-center justify-between rounded-xl border border-neutral-800 bg-neutral-950 px-3 py-2.5">
      <span className="text-sm text-neutral-300">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-neutral-500">{settings[k]}</span>
        <input type="color" value={settings[k]} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
          data-testid={`branding-${k}`} className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent" />
      </div>
    </div>
  );

  return (
    <div>
      <StepHeader title="Dein Branding" subtitle="Logo und Farben – sofort in der Vorschau sichtbar." />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <Label className="text-neutral-300">Studio-Name</Label>
            <Input value={settings.studio_name} onChange={(e) => setSettings({ ...settings, studio_name: e.target.value })}
              data-testid="branding-studio-name" className="mt-1 border-neutral-700 bg-neutral-950 text-neutral-100" />
          </div>
          <div>
            <Label className="text-neutral-300">Logo</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="branding-logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="branding-logo-btn" className="mt-1 w-full border-neutral-700 bg-neutral-950 text-neutral-200 hover:bg-neutral-800">
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Logo hochladen
            </Button>
          </div>
          {colorField('primary_color', 'Primärfarbe')}
          {colorField('secondary_color', 'Sekundärfarbe')}
          {colorField('accent_color', 'Akzentfarbe')}
        </div>

        {/* Live-Vorschau */}
        <div className="rounded-2xl border border-neutral-800 p-4" style={{ background: `linear-gradient(135deg, ${settings.primary_color}22, ${settings.secondary_color}11)` }}>
          <p className="mb-3 text-xs uppercase tracking-wide text-neutral-500">Vorschau</p>
          <div className="rounded-xl bg-white p-5 text-neutral-900 shadow-lg" data-testid="branding-preview">
            <div className="flex items-center gap-3">
              {settings.logo_url ? (
                <img src={process.env.REACT_APP_BACKEND_URL + settings.logo_url} alt="Logo" className="h-10 w-10 rounded-lg object-contain" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ background: settings.primary_color }}>
                  {(settings.studio_name || 'S')[0]}
                </div>
              )}
              <div>
                <div className="font-semibold">{settings.studio_name}</div>
                <div className="text-xs text-neutral-500">Kundenportal</div>
              </div>
            </div>
            <button className="mt-4 w-full rounded-lg py-2.5 text-sm font-medium text-white" style={{ background: settings.primary_color }}>
              Termin buchen
            </button>
            <div className="mt-2 flex gap-2">
              <span className="rounded-md px-2 py-1 text-xs text-white" style={{ background: settings.secondary_color }}>Aktiv</span>
              <span className="rounded-md px-2 py-1 text-xs text-white" style={{ background: settings.accent_color }}>Neu</span>
            </div>
          </div>
        </div>
      </div>
      <NavRow onBack={onBack}>
        <Button disabled={saving} onClick={save} data-testid="branding-next-btn"
          className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Weiter <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </NavRow>
    </div>
  );
}

function ModuleStep({ data, onBack, onNext }) {
  const [modules, setModules] = useState([]);
  const [interval, setInterval] = useState(data.modules?.interval || 'month');
  const [selected, setSelected] = useState(new Set(data.modules?.module_keys || []));
  const [trialDays, setTrialDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/subscription`).then(({ data: s }) => {
      const available = (s.modules || []).filter((m) => m.active !== false);
      setModules(available);
      setTrialDays(s.trial_days ?? 14);
      if (!(data.modules?.module_keys)) {
        const availKeys = new Set(available.map((m) => m.key));
        const starter = (data.starter_module_keys || []).filter((k) => availKeys.has(k));
        if (starter.length) setSelected(new Set(starter));
        else if (s.subscription?.module_keys?.length) setSelected(new Set(s.subscription.module_keys.filter((k) => availKeys.has(k))));
      }
    }).catch(() => toast.error('Module konnten nicht geladen werden')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (k) => {
    const n = new Set(selected);
    n.has(k) ? n.delete(k) : n.add(k);
    setSelected(n);
  };

  const priceOf = (m) => interval === 'year' ? (m.yearly_chf || 0) : (m.monthly_chf || 0);
  const total = modules.filter((m) => selected.has(m.key)).reduce((a, m) => a + priceOf(m), 0);

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />;

  return (
    <div>
      <StepHeader title="Module wählen" subtitle={`Nur zahlen, was du nutzt. ${trialDays} Tage kostenlos testen.`} />
      {data.branche_label && (
        <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-200" data-testid="module-starter-hint">
          Startpaket für <span className="font-medium">{data.branche_label}</span> vorausgewählt – du kannst frei anpassen.
        </div>
      )}
      <div className="mb-4 inline-flex rounded-xl bg-neutral-800 p-1" data-testid="module-interval-toggle">
        {['month', 'year'].map((iv) => (
          <button key={iv} onClick={() => setInterval(iv)} data-testid={`interval-${iv}`}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
              interval === iv ? 'bg-emerald-500 text-neutral-950' : 'text-neutral-300'}`}>
            {iv === 'month' ? 'Monatlich' : 'Jährlich'}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {modules.map((m) => {
          const on = selected.has(m.key);
          return (
            <button key={m.key} onClick={() => toggle(m.key)} data-testid={`module-card-${m.key}`}
              className={`rounded-2xl border p-4 text-left transition-all ${
                on ? 'border-emerald-500 bg-emerald-500/10' : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-neutral-100">{m.name}</div>
                <div className={`flex h-5 w-5 flex-none items-center justify-center rounded-md ${on ? 'bg-emerald-500 text-neutral-950' : 'border border-neutral-600'}`}>
                  {on && <Check className="h-3.5 w-3.5" />}
                </div>
              </div>
              <div className="mt-2 text-xs text-neutral-400 line-clamp-2">{m.description}</div>
              <div className="mt-3 text-sm font-semibold text-emerald-400">
                CHF {priceOf(m).toFixed(2)}<span className="text-xs font-normal text-neutral-500">/{interval === 'year' ? 'Jahr' : 'Monat'}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between rounded-2xl border border-neutral-800 bg-neutral-950 px-4 py-3">
        <span className="text-sm text-neutral-400">Gesamt ({selected.size} Module)</span>
        <span className="text-lg font-semibold text-neutral-100" data-testid="module-total">
          CHF {total.toFixed(2)}<span className="text-sm font-normal text-neutral-500">/{interval === 'year' ? 'Jahr' : 'Monat'}</span>
        </span>
      </div>

      <NavRow onBack={onBack}>
        <Button disabled={selected.size === 0} onClick={() => onNext({ module_keys: [...selected], interval })}
          data-testid="module-next-btn" className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
          Weiter <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </NavRow>
    </div>
  );
}

function WechselStep({ data, onBack, onNext, reload }) {
  const [system, setSystem] = useState(data.migration?.system || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(data.migration?.mode === 'import' ? data.migration : null);
  const [reviewDone, setReviewDone] = useState(data.migration?.mode === 'review');
  const importRef = useRef(null);
  const reviewRef = useRef(null);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: r } = await axios.post(`${API}/einrichtung/migration/import`, fd);
      setResult(r);
      toast.success(`${r.imported} Kunden importiert`);
      reload && reload();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Import fehlgeschlagen');
    } finally { setBusy(false); if (importRef.current) importRef.current.value = ''; }
  };

  const doReview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('system', system);
      await axios.post(`${API}/einrichtung/migration/review`, fd);
      setReviewDone(true);
      toast.success('Zur Prüfung an das Team übermittelt');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setBusy(false); if (reviewRef.current) reviewRef.current.value = ''; }
  };

  return (
    <div>
      <StepHeader title="Daten aus altem System" subtitle="Optional – du kannst diesen Schritt auch überspringen." />
      <div className="mb-5">
        <Label className="text-neutral-300">Bisheriges System</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {WECHSEL_SYSTEME.map((s) => (
            <button key={s} onClick={() => setSystem(s)} data-testid={`wechsel-system-${s}`}
              className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                system === s ? 'border-emerald-500 bg-emerald-500/10 text-white' : 'border-neutral-700 bg-neutral-900 text-neutral-300'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Echter CSV-Import */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-5">
          <div className="flex items-center gap-2 text-neutral-100"><Upload className="h-4 w-4 text-emerald-400" /><span className="font-medium">Jetzt importieren</span></div>
          <p className="mt-1 text-xs text-neutral-400">CSV mit Spalten wie Vorname, Nachname, E-Mail, Telefon. Wird sofort eingelesen.</p>
          <input ref={importRef} type="file" accept=".csv,text/csv" onChange={doImport} className="hidden" data-testid="wechsel-import-input" />
          <Button variant="outline" disabled={busy} onClick={() => importRef.current?.click()} data-testid="wechsel-import-btn"
            className="mt-3 w-full border-neutral-700 bg-neutral-950 text-neutral-200 hover:bg-neutral-800">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} CSV auswählen
          </Button>
          {result && (
            <div className="mt-3 rounded-lg bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300" data-testid="wechsel-import-result">
              {result.imported} importiert · {result.skipped} übersprungen{result.errors ? ` · ${result.errors} Fehler` : ''}
            </div>
          )}
        </div>

        {/* Zur Prüfung */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-5">
          <div className="flex items-center gap-2 text-neutral-100"><FileCheck2 className="h-4 w-4 text-emerald-400" /><span className="font-medium">Vom Team prüfen lassen</span></div>
          <p className="mt-1 text-xs text-neutral-400">Lade einen Export/Nachweis hoch – wir übernehmen die Daten für dich.</p>
          <input ref={reviewRef} type="file" onChange={doReview} className="hidden" data-testid="wechsel-review-input" />
          <Button variant="outline" disabled={busy} onClick={() => reviewRef.current?.click()} data-testid="wechsel-review-btn"
            className="mt-3 w-full border-neutral-700 bg-neutral-950 text-neutral-200 hover:bg-neutral-800">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Datei hochladen
          </Button>
          {reviewDone && (
            <div className="mt-3 rounded-lg bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300" data-testid="wechsel-review-result">
              Übermittelt – unser Team meldet sich.
            </div>
          )}
        </div>
      </div>

      <NavRow onBack={onBack}>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onNext} data-testid="wechsel-skip-btn" className="text-neutral-400 hover:text-white">Überspringen</Button>
          <Button onClick={onNext} data-testid="wechsel-next-btn" className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
            Weiter <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </NavRow>
    </div>
  );
}

function ZahlungStep({ data, subStatus, onBack, onDone }) {
  const [agb, setAgb] = useState(false);
  const [avv, setAvv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [modules, setModules] = useState([]);
  const sel = data.modules?.module_keys || [];
  const interval = data.modules?.interval || 'month';

  useEffect(() => {
    axios.get(`${API}/admin/subscription`).then(({ data: s }) => {
      setModules((s.modules || []).filter((m) => sel.includes(m.key)));
      setTrialDays(s.trial_days ?? 14);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceOf = (m) => interval === 'year' ? (m.yearly_chf || 0) : (m.monthly_chf || 0);
  const total = modules.reduce((a, m) => a + priceOf(m), 0);
  const alreadyActive = subStatus === 'trialing' || subStatus === 'active';

  const startCheckout = async () => {
    if (!agb || !avv) { toast.error('Bitte AGB und Auftragsverarbeitung bestätigen'); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/einrichtung/payment/checkout`, {
        module_keys: sel, interval, origin_url: window.location.origin,
      });
      if (r.checkout_url) window.location.href = r.checkout_url;
      else { toast.error('Keine Zahlungs-URL erhalten'); setBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zahlung konnte nicht gestartet werden');
      setBusy(false);
    }
  };

  return (
    <div>
      <StepHeader title="Testmonat starten" subtitle={`${trialDays} Tage kostenlos. Karte hinterlegen – Abbuchung erst nach der Testphase.`} />

      <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
        <div className="space-y-2">
          {modules.map((m) => (
            <div key={m.key} className="flex items-center justify-between text-sm">
              <span className="text-neutral-300">{m.name}</span>
              <span className="text-neutral-400">CHF {priceOf(m).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-neutral-800 pt-3">
          <span className="text-sm text-neutral-400">Nach dem Testmonat</span>
          <span className="font-semibold text-neutral-100" data-testid="zahlung-total">
            CHF {total.toFixed(2)}<span className="text-xs font-normal text-neutral-500">/{interval === 'year' ? 'Jahr' : 'Monat'}</span>
          </span>
        </div>
      </div>

      {alreadyActive ? (
        <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="zahlung-active">
          <Check className="mx-auto h-8 w-8 text-emerald-400" />
          <p className="mt-2 font-medium text-emerald-200">Testmonat aktiv – Karte hinterlegt.</p>
          <Button onClick={onDone} data-testid="zahlung-continue-btn" className="mt-4 bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
            Weiter <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-5 space-y-3">
            <label className="flex items-start gap-3 text-sm text-neutral-300">
              <Checkbox checked={agb} onCheckedChange={setAgb} data-testid="zahlung-agb" className="mt-0.5" />
              <span>Ich akzeptiere die <a href="/agb" target="_blank" className="text-emerald-400 underline">AGB</a>.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-neutral-300">
              <Checkbox checked={avv} onCheckedChange={setAvv} data-testid="zahlung-avv" className="mt-0.5" />
              <span>Ich akzeptiere den Auftragsverarbeitungsvertrag (AVV) gem. DSG.</span>
            </label>
          </div>
          <NavRow onBack={onBack}>
            <Button disabled={busy || sel.length === 0} onClick={startCheckout} data-testid="zahlung-checkout-btn"
              className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />} Jetzt kostenlos testen
            </Button>
          </NavRow>
        </>
      )}
    </div>
  );
}

function FertigStep({ navigate }) {
  const [busy, setBusy] = useState(false);
  const finish = async (to) => {
    setBusy(true);
    try { await axios.post(`${API}/einrichtung/complete`); }
    catch (e) { toast.error('Abschluss konnte nicht gespeichert werden'); }
    navigate(to);
  };
  return (
    <div className="py-6 text-center" data-testid="setup-fertig">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30">
        <CalendarCheck className="h-8 w-8 text-emerald-400" />
      </div>
      <h2 className="mt-5 text-2xl font-semibold text-neutral-100">Geschafft – dein Studio ist startklar!</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">
        Du kannst jetzt deinen Kalender öffnen, Kunden anlegen und Termine entgegennehmen.
      </p>
      <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button disabled={busy} onClick={() => finish('/appointments')} data-testid="fertig-calendar-btn"
          className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarCheck className="mr-2 h-4 w-4" />} Zum Kalender
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => finish('/dashboard')} data-testid="fertig-dashboard-btn"
          className="border-neutral-700 bg-neutral-950 text-neutral-200 hover:bg-neutral-800">
          Zum Dashboard
        </Button>
      </div>
    </div>
  );
}
